/**
 * The blog article.
 *
 * Everything here is an upgrade on markup that already works. The share links
 * are real hrefs built server-side from the article's own address, so they
 * open the network's share page with the module missing; this only turns them
 * into a sized popup, adds copy-link, and opens the cover in a lightbox.
 *
 * Nothing is sent anywhere except by the visitor's own click.
 */
class ItBlogPost extends HTMLElement {
  #toastTimer = 0;
  #lastFocus = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKey);
    clearTimeout(this.#toastTimer);
  }

  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  #onClick = (event) => {
    const share = event.target.closest('.it-post__share');
    if (share) {
      // A modified click is the visitor asking for a tab of their own.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      window.open(share.href, '_blank', 'noopener,width=640,height=560');
      return;
    }

    if (event.target.closest('[data-copy]')) {
      this.#copyLink();
      return;
    }

    if (event.target.closest('[ref="zoom"]') || event.target.closest('[ref="hero"]')) {
      this.#openLightbox();
      return;
    }

    const lightbox = this.#ref('lightbox');
    if (!lightbox || lightbox.hidden) return;
    if (event.target.closest('[ref="lightboxClose"]') || event.target === lightbox) {
      this.#closeLightbox();
    }
  };

  #onKey = (event) => {
    if (event.key === 'Escape') this.#closeLightbox();
  };

  async #copyLink() {
    const url = location.href.split('#')[0];
    try {
      await navigator.clipboard.writeText(url);
      this.#say('Link copied');
    } catch {
      // Insecure origin, denied permission, no API — the address bar still works.
      this.#say('Copy the address bar to share');
    }
  }

  #openLightbox() {
    const lightbox = this.#ref('lightbox');
    const hero = this.#ref('hero');
    const image = this.#ref('lightboxImage');
    if (!lightbox || !hero || !image) return;

    image.src = hero.currentSrc || hero.src;
    image.alt = hero.alt;
    lightbox.hidden = false;

    // One forced frame so the transition has a from-state.
    void lightbox.offsetHeight;
    lightbox.classList.add('is-open');

    this.#lastFocus = document.activeElement;
    this.#ref('lightboxClose')?.focus();
  }

  #closeLightbox() {
    const lightbox = this.#ref('lightbox');
    if (!lightbox || lightbox.hidden) return;

    lightbox.classList.remove('is-open');
    setTimeout(() => {
      lightbox.hidden = true;
    }, 320);

    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
    this.#lastFocus = null;
  }

  /**
   * @param {string} message - Short line to flash at the foot of the page
   */
  #say(message) {
    const toast = this.#ref('toast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-on');
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => toast.classList.remove('is-on'), 2200);
  }
}

if (!customElements.get('it-blog-post')) {
  customElements.define('it-blog-post', ItBlogPost);
}
